// src/pages/AdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const SIDEBAR_BG = '#0f172a';
const ACCENT = '#6366f1';
const ACCENT_LIGHT = '#818cf8';
const SIDEBAR_TEXT = '#94a3b8';
const SIDEBAR_ACTIVE_BG = 'rgba(99,102,241,0.15)';
const CARD_BG = '#ffffff';
const PAGE_BG = '#f1f5f9';

const styles = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: PAGE_BG,
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  },
  sidebar: {
    width: 260,
    minHeight: '100vh',
    background: SIDEBAR_BG,
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 100,
    boxShadow: '4px 0 24px rgba(0,0,0,0.18)',
  },
  sidebarLogo: {
    padding: '28px 24px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  logoTitle: {
    color: '#ffffff',
    fontWeight: 800,
    fontSize: '1.2rem',
    letterSpacing: '-0.5px',
    margin: 0,
  },
  logoSub: {
    color: SIDEBAR_TEXT,
    fontSize: '0.7rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    marginTop: 2,
  },
  navSection: {
    padding: '20px 12px 8px',
    flex: 1,
    overflowY: 'auto',
  },
  navLabel: {
    color: 'rgba(148,163,184,0.5)',
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    padding: '0 12px',
    marginBottom: 6,
    marginTop: 16,
  },
  navItem: (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 10,
    cursor: 'pointer',
    marginBottom: 2,
    background: active ? SIDEBAR_ACTIVE_BG : 'transparent',
    color: active ? '#ffffff' : SIDEBAR_TEXT,
    fontWeight: active ? 600 : 400,
    fontSize: '0.875rem',
    transition: 'all 0.15s ease',
    border: active ? `1px solid rgba(99,102,241,0.3)` : '1px solid transparent',
  }),
  navIcon: (active) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: active ? ACCENT : 'rgba(255,255,255,0.05)',
    color: active ? '#fff' : SIDEBAR_TEXT,
    fontSize: '0.8rem',
    flexShrink: 0,
  }),
  badge: (color) => ({
    marginLeft: 'auto',
    background: color || 'rgba(99,102,241,0.2)',
    color: color ? '#fff' : ACCENT_LIGHT,
    borderRadius: 20,
    padding: '1px 7px',
    fontSize: '0.7rem',
    fontWeight: 700,
    minWidth: 20,
    textAlign: 'center',
  }),
  sidebarFooter: {
    padding: '16px 12px',
    borderTop: '1px solid rgba(255,255,255,0.06)',
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: 'none',
    background: 'rgba(239,68,68,0.1)',
    color: '#f87171',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    transition: 'all 0.15s',
  },
  main: {
    marginLeft: 260,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  topbar: {
    background: CARD_BG,
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #e2e8f0',
    position: 'sticky',
    top: 0,
    zIndex: 50,
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: '1.1rem',
    color: '#0f172a',
    margin: 0,
  },
  pageSubtitle: {
    color: '#64748b',
    fontSize: '0.8rem',
    margin: 0,
  },
  content: {
    padding: '28px 32px',
    flex: 1,
  },
  card: {
    background: CARD_BG,
    borderRadius: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.06)',
    overflow: 'hidden',
  },
  statCard: (color, bg) => ({
    background: CARD_BG,
    borderRadius: 16,
    padding: '20px 24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    borderLeft: `4px solid ${color}`,
  }),
  statIcon: (bg) => ({
    width: 48,
    height: 48,
    borderRadius: 12,
    background: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.2rem',
    flexShrink: 0,
  }),
  tableHead: {
    background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  th: {
    padding: '12px 16px',
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '14px 16px',
    fontSize: '0.875rem',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
    verticalAlign: 'middle',
  },
  pill: (bg, color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: '0.72rem',
    fontWeight: 600,
    background: bg,
    color: color,
    whiteSpace: 'nowrap',
  }),
  btn: (variant) => {
    const variants = {
      primary: { background: ACCENT, color: '#fff', border: 'none' },
      success: { background: '#10b981', color: '#fff', border: 'none' },
      danger: { background: 'transparent', color: '#ef4444', border: '1px solid #ef4444' },
      warning: { background: 'transparent', color: '#f59e0b', border: '1px solid #f59e0b' },
      ghost: { background: '#f1f5f9', color: '#475569', border: 'none' },
    };
    return {
      ...variants[variant],
      padding: '6px 14px',
      borderRadius: 8,
      fontSize: '0.78rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.15s',
      whiteSpace: 'nowrap',
    };
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#94a3b8',
  },
  filterBtn: (active) => ({
    padding: '6px 14px',
    borderRadius: 8,
    border: active ? `1px solid ${ACCENT}` : '1px solid #e2e8f0',
    background: active ? ACCENT : '#fff',
    color: active ? '#fff' : '#64748b',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  }),
  modal: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999,
    padding: 20,
  },
  modalBox: {
    background: '#fff',
    borderRadius: 20,
    width: '100%',
    maxWidth: 520,
    boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
    overflow: 'hidden',
  },
  modalBoxLg: {
    background: '#fff',
    borderRadius: 20,
    width: '100%',
    maxWidth: 720,
    boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalHeader: {
    padding: '20px 24px',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modalBody: { padding: '20px 24px' },
  modalFooter: {
    padding: '16px 24px',
    borderTop: '1px solid #f1f5f9',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
  avatar: (bg, color) => ({
    width: 34,
    height: 34,
    borderRadius: 10,
    background: bg,
    color: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '0.85rem',
    flexShrink: 0,
  }),
  input: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    color: '#0f172a',
    outline: 'none',
    background: '#fff',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    color: '#0f172a',
    outline: 'none',
    background: '#fff',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    color: '#0f172a',
    outline: 'none',
    background: '#fff',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  label: {
    display: 'block',
    fontWeight: 600,
    fontSize: '0.8rem',
    color: '#374151',
    marginBottom: 6,
  },
  formGroup: { marginBottom: 16 },
  infoBox: (bg, color) => ({
    background: bg,
    color: color,
    borderRadius: 10,
    padding: '12px 16px',
    fontSize: '0.82rem',
    marginBottom: 16,
    lineHeight: 1.6,
  }),
};

function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionVideos, setSessionVideos] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [eduCoins, setEduCoins] = useState(null);
  const [trcnRegistrations, setTrcnRegistrations] = useState([]);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showTRCNModal, setShowTRCNModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [selectedTRCN, setSelectedTRCN] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [trcnForm, setTrcnForm] = useState({ status: '', examDate: '', notes: '' });
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [sessionStatusFilter, setSessionStatusFilter] = useState('all');

  useEffect(() => {
    const userType = localStorage.getItem('userType');
    const userId = localStorage.getItem('userId');
    if (!userId || userType !== 'admin') navigate('/');
  }, [navigate]);

  const fetchStats = useCallback(async () => {
    try { const res = await api.get('/admin/stats'); setStats(res.data.stats); } catch (e) { console.error(e); }
  }, []);
  const fetchUsers = useCallback(async (type) => {
    try {
      const url = type && type !== 'all' ? `/admin/users?userType=${type}` : '/admin/users';
      const res = await api.get(url); setUsers(res.data.users || []);
    } catch (e) { console.error(e); }
  }, []);
  const fetchSessions = useCallback(async (status) => {
    try {
      const url = status && status !== 'all' ? `/admin/sessions?status=${status}` : '/admin/sessions';
      const res = await api.get(url); setSessions(res.data.sessions || []);
    } catch (e) { console.error(e); }
  }, []);
  const fetchSessionVideos = useCallback(async () => {
    try { const res = await api.get('/admin/session-videos'); setSessionVideos(res.data.sessions || []); } catch (e) { console.error(e); }
  }, []);
  const fetchWithdrawals = useCallback(async () => {
    try { const res = await api.get('/admin/withdrawals'); setWithdrawals(res.data.withdrawals || []); } catch (e) { console.error(e); }
  }, []);
  const fetchEduCoins = useCallback(async () => {
    try { const res = await api.get('/admin/edu-coins'); setEduCoins(res.data.eduCoins); } catch (e) { console.error(e); }
  }, []);
  const fetchTRCN = useCallback(async () => {
    try { const res = await api.get('/admin/trcn-registrations'); setTrcnRegistrations(res.data.registrations || []); } catch (e) { console.error(e); }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.allSettled([fetchStats(), fetchUsers(), fetchSessions(), fetchSessionVideos(), fetchWithdrawals(), fetchEduCoins(), fetchTRCN()]);
    setLoading(false);
  }, [fetchStats, fetchUsers, fetchSessions, fetchSessionVideos, fetchWithdrawals, fetchEduCoins, fetchTRCN]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleVerifyTeacher = async (teacherId) => {
    if (!window.confirm('Verify this teacher?')) return;
    setActionLoading(true);
    try {
      await api.put(`/admin/teachers/${teacherId}/verify`);
      alert('Teacher verified!');
      await Promise.all([fetchUsers(userTypeFilter), fetchStats(), fetchTRCN()]);
    } catch (e) { alert(e.response?.data?.message || e.message); }
    finally { setActionLoading(false); }
  };

  const handleSuspendUser = async (userId) => {
    if (!window.confirm('Suspend this user?')) return;
    setActionLoading(true);
    try { await api.put(`/admin/users/${userId}/suspend`); await fetchUsers(userTypeFilter); }
    catch (e) { alert(e.response?.data?.message || e.message); }
    finally { setActionLoading(false); }
  };

  const handleActivateUser = async (userId) => {
    setActionLoading(true);
    try { await api.put(`/admin/users/${userId}/activate`); await fetchUsers(userTypeFilter); }
    catch (e) { alert(e.response?.data?.message || e.message); }
    finally { setActionLoading(false); }
  };

  const handleDeleteUser = async (userId, name) => {
    if (!window.confirm(`Permanently delete ${name}?`)) return;
    setActionLoading(true);
    try { await api.delete(`/admin/users/${userId}`); await Promise.all([fetchUsers(userTypeFilter), fetchStats()]); }
    catch (e) { alert(e.response?.data?.message || e.message); }
    finally { setActionLoading(false); }
  };

  const handleApproveWithdrawal = async (id) => {
    if (!window.confirm('Approve this withdrawal?')) return;
    setActionLoading(true);
    try { await api.put(`/admin/withdrawals/${id}/approve`); await Promise.all([fetchWithdrawals(), fetchStats()]); }
    catch (e) { alert(e.response?.data?.message || e.message); }
    finally { setActionLoading(false); }
  };

  const confirmRejectVerification = async () => {
    setActionLoading(true);
    try {
      await api.put(`/admin/teachers/${selectedUser.id}/reject-verification`, { reason: rejectionReason });
      setShowRejectModal(false); setSelectedUser(null); setRejectionReason('');
      await Promise.all([fetchUsers(userTypeFilter), fetchTRCN()]);
    } catch (e) { alert(e.response?.data?.message || e.message); }
    finally { setActionLoading(false); }
  };

  const confirmRejectWithdrawal = async () => {
    setActionLoading(true);
    try {
      await api.put(`/admin/withdrawals/${selectedWithdrawal.id}/reject`, { reason: rejectionReason });
      setShowRejectModal(false); setSelectedWithdrawal(null); setRejectionReason('');
      await Promise.all([fetchWithdrawals(), fetchStats()]);
    } catch (e) { alert(e.response?.data?.message || e.message); }
    finally { setActionLoading(false); }
  };

  const handleUpdateTRCN = async () => {
    if (!trcnForm.status) { alert('Please select a status.'); return; }
    setActionLoading(true);
    try {
      await api.put(`/admin/trcn-registrations/${selectedTRCN.id}/status`, trcnForm);
      setShowTRCNModal(false); setSelectedTRCN(null);
      await fetchTRCN();
    } catch (e) { alert(e.response?.data?.message || e.message); }
    finally { setActionLoading(false); }
  };

  const closeRejectModal = () => { setShowRejectModal(false); setRejectionReason(''); setSelectedUser(null); setSelectedWithdrawal(null); };

  const formatDate = (ts) => {
    if (!ts) return '—';
    try { const d = ts.toDate ? ts.toDate() : new Date(ts); return d.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return '—'; }
  };

  const teachers = users.filter(u => u.userType === 'teacher');
  const filteredUsers = userTypeFilter === 'all' ? users : users.filter(u => u.userType === userTypeFilter);
  const filteredSessions = sessionStatusFilter === 'all' ? sessions : sessions.filter(s => s.status === sessionStatusFilter);

  const NAV = [
    { section: 'Main' },
    { key: 'overview', icon: 'fa-chart-pie', label: 'Overview' },
    { section: 'People' },
    { key: 'users', icon: 'fa-users', label: 'All Users', count: users.length },
    { key: 'teachers', icon: 'fa-chalkboard-teacher', label: 'Teachers', count: teachers.length },
    { key: 'trcn', icon: 'fa-id-card', label: 'TRCN Exams', count: trcnRegistrations.length, countColor: trcnRegistrations.length > 0 ? '#f59e0b' : null },
    { section: 'Activity' },
    { key: 'sessions', icon: 'fa-calendar-check', label: 'Sessions', count: sessions.length },
    { key: 'videos', icon: 'fa-video', label: 'Session Videos', count: sessionVideos.length },
    { section: 'Finance' },
    { key: 'withdrawals', icon: 'fa-money-bill-wave', label: 'Withdrawals', count: withdrawals.length, countColor: withdrawals.length > 0 ? '#ef4444' : null },
    { key: 'educoins', icon: 'fa-coins', label: 'EDU Coins' },
  ];

  const PAGE_META = {
    overview: { title: 'Overview', subtitle: 'Platform-wide stats at a glance' },
    users: { title: 'All Users', subtitle: 'Manage students, teachers and parents' },
    teachers: { title: 'Teachers', subtitle: 'Verification and account management' },
    trcn: { title: 'TRCN Exam Registrations', subtitle: 'Teachers pending exam scheduling' },
    sessions: { title: 'Sessions', subtitle: 'All teaching sessions on the platform' },
    videos: { title: 'Session Videos', subtitle: 'Videos uploaded by teachers after sessions' },
    withdrawals: { title: 'Withdrawal Requests', subtitle: 'Pending payout requests from teachers' },
    educoins: { title: 'EDU Coins', subtitle: 'Coin issuance and wallet analytics' },
  };

  if (loading) {
    return (
      <div style={{ ...styles.layout, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, border: `3px solid ${ACCENT}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }}></div>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading dashboard...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.layout}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        body { margin: 0; }
        .nav-item:hover { background: rgba(99,102,241,0.08) !important; color: #e2e8f0 !important; }
        .action-btn:hover { opacity: 0.85; }
        .logout-btn:hover { background: rgba(239,68,68,0.2) !important; }
        ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
        table { border-collapse: collapse; width: 100%; }
        .filter-btn:hover { border-color: ${ACCENT} !important; color: ${ACCENT} !important; }
      `}</style>

      {/* SIDEBAR */}
      <aside style={styles.sidebar}>
        {/* Logo */}
        <div style={styles.sidebarLogo}>
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <div style={{ width: 36, height: 36, background: `linear-gradient(135deg, ${ACCENT}, #8b5cf6)`, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <img
        src="/logo.png"
        alt="EduSpace Logo"
        style={{
          height: "28px",
          width: "28px",
          objectFit: "contain",
          filter: "brightness(0) invert(1)"
        }}
      />
    </div>
    <div>
      <p style={styles.logoTitle}>EduLinker</p>
      <p style={styles.logoSub}>Admin Console</p>
    </div>
  </div>
</div>

        {/* Nav */}
        <nav style={styles.navSection}>
          {NAV.map((item, i) => {
            if (item.section) return (
              <p key={i} style={styles.navLabel}>{item.section}</p>
            );
            const active = activeTab === item.key;
            return (
              <div key={item.key} className="nav-item" style={styles.navItem(active)}
                onClick={() => setActiveTab(item.key)}>
                <div style={styles.navIcon(active)}>
                  <i className={`fas ${item.icon}`}></i>
                </div>
                <span>{item.label}</span>
                {item.count !== undefined && item.count > 0 && (
                  <span style={styles.badge(item.countColor)}>{item.count}</span>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div style={styles.sidebarFooter}>
          <div style={{ padding: '8px 12px', marginBottom: 8 }}>
            <p style={{ color: SIDEBAR_TEXT, fontSize: '0.72rem', margin: 0 }}>Signed in as</p>
            <p style={{ color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 600, margin: 0 }}>Admin</p>
          </div>
          <button className="logout-btn" style={styles.logoutBtn}
            onClick={() => { localStorage.clear(); navigate('/login'); }}>
            <i className="fas fa-sign-out-alt"></i>
            Sign out
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main style={styles.main}>
        {/* Topbar */}
        <div style={styles.topbar}>
          <div>
            <h1 style={styles.pageTitle}>{PAGE_META[activeTab]?.title}</h1>
            <p style={styles.pageSubtitle}>{PAGE_META[activeTab]?.subtitle}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
              {new Date().toLocaleDateString('en-NG', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            <button className="action-btn" style={{ ...styles.btn('ghost'), display: 'flex', alignItems: 'center', gap: 6 }} onClick={loadAll}>
              <i className="fas fa-sync" style={{ fontSize: '0.75rem' }}></i> Refresh
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={styles.content}>

          {/* ====== OVERVIEW ====== */}
          {activeTab === 'overview' && stats && (
            <div>
              {/* Stats grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                {[
                  { label: 'Teachers', value: stats.users.teachers, icon: 'fa-chalkboard-teacher', color: '#6366f1', bg: '#eef2ff' },
                  { label: 'Students', value: stats.users.students, icon: 'fa-user-graduate', color: '#10b981', bg: '#ecfdf5' },
                  { label: 'Parents', value: stats.users.parents, icon: 'fa-users', color: '#0891b2', bg: '#ecfeff' },
                  { label: 'Unverified Teachers', value: stats.users.unverifiedTeachers, icon: 'fa-user-clock', color: '#f59e0b', bg: '#fffbeb' },
                ].map((c, i) => (
                  <div key={i} style={styles.statCard(c.color, c.bg)}>
                    <div style={styles.statIcon(c.bg)}>
                      <i className={`fas ${c.icon}`} style={{ color: c.color }}></i>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{c.value}</div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 4 }}>{c.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                {[
                  { label: 'Total Sessions', value: stats.sessions.total, icon: 'fa-calendar', color: '#6366f1', bg: '#eef2ff' },
                  { label: 'Completed', value: stats.sessions.completed, icon: 'fa-check-circle', color: '#10b981', bg: '#ecfdf5' },
                  { label: 'Scheduled', value: stats.sessions.scheduled, icon: 'fa-calendar-check', color: '#0891b2', bg: '#ecfeff' },
                  { label: 'Ongoing Now', value: stats.sessions.ongoing, icon: 'fa-circle', color: '#ef4444', bg: '#fef2f2' },
                ].map((c, i) => (
                  <div key={i} style={styles.statCard(c.color, c.bg)}>
                    <div style={styles.statIcon(c.bg)}>
                      <i className={`fas ${c.icon}`} style={{ color: c.color }}></i>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{c.value}</div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 4 }}>{c.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                {[
                  { label: 'Total Naira Earnings', value: `₦${(stats.finance.totalNairaEarnings || 0).toLocaleString()}`, icon: 'fa-naira-sign', color: '#10b981', bg: '#ecfdf5' },
                  { label: 'Pending Withdrawals', value: `₦${(stats.finance.totalPendingWithdrawals || 0).toLocaleString()}`, icon: 'fa-hourglass-half', color: '#f59e0b', bg: '#fffbeb' },
                  { label: 'EDU Coins Issued', value: `${(stats.finance.totalEduCoinsIssued || 0).toLocaleString()}`, icon: 'fa-coins', color: '#7c3aed', bg: '#f5f3ff' },
                  { label: 'EDU in Wallets', value: `${(stats.finance.totalEduCoinsInWallets || 0).toLocaleString()}`, icon: 'fa-wallet', color: '#0891b2', bg: '#ecfeff' },
                ].map((c, i) => (
                  <div key={i} style={styles.statCard(c.color, c.bg)}>
                    <div style={styles.statIcon(c.bg)}>
                      <i className={`fas ${c.icon}`} style={{ color: c.color }}></i>
                    </div>
                    <div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{c.value}</div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 4 }}>{c.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ====== USERS ====== */}
          {activeTab === 'users' && (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {['all', 'student', 'teacher', 'parent'].map(t => (
                  <button key={t} className="filter-btn" style={styles.filterBtn(userTypeFilter === t)}
                    onClick={() => { setUserTypeFilter(t); fetchUsers(t); }}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
              <div style={styles.card}>
                <table>
                  <thead style={styles.tableHead}>
                    <tr>
                      {['Name', 'Email', 'Type', 'State', 'EDU Coins', 'Joined', 'Status', 'Actions'].map(h => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.id}>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={styles.avatar('#eef2ff', '#4f46e5')}>
                              {(user.firstName || user.name || 'U').charAt(0)}
                            </div>
                            <span style={{ fontWeight: 500 }}>
                              {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td style={{ ...styles.td, color: '#64748b', fontSize: '0.8rem' }}>{user.email}</td>
                        <td style={styles.td}>
                          <span style={styles.pill(
                            user.userType === 'teacher' ? '#eef2ff' : user.userType === 'student' ? '#ecfdf5' : '#ecfeff',
                            user.userType === 'teacher' ? '#4f46e5' : user.userType === 'student' ? '#059669' : '#0891b2'
                          )}>{user.userType}</span>
                        </td>
                        <td style={{ ...styles.td, color: '#64748b', fontSize: '0.8rem' }}>{user.state || '—'}</td>
                        <td style={styles.td}><span style={{ color: '#7c3aed', fontWeight: 600 }}>{user.tokenBalance || 0}</span></td>
                        <td style={{ ...styles.td, color: '#64748b', fontSize: '0.8rem' }}>{formatDate(user.createdAt)}</td>
                        <td style={styles.td}>
                          <span style={styles.pill(user.status === 'suspended' ? '#fef2f2' : '#ecfdf5', user.status === 'suspended' ? '#ef4444' : '#10b981')}>
                            {user.status || 'active'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {user.status === 'suspended'
                              ? <button className="action-btn" style={styles.btn('success')} onClick={() => handleActivateUser(user.id)} disabled={actionLoading}>Activate</button>
                              : <button className="action-btn" style={styles.btn('warning')} onClick={() => handleSuspendUser(user.id)} disabled={actionLoading}>Suspend</button>
                            }
                            <button className="action-btn" style={styles.btn('danger')}
                              onClick={() => handleDeleteUser(user.id, `${user.firstName} ${user.lastName}`)} disabled={actionLoading}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredUsers.length === 0 && <div style={styles.emptyState}><i className="fas fa-users" style={{ fontSize: '2rem', marginBottom: 12, display: 'block' }}></i>No users found</div>}
              </div>
            </div>
          )}

          {/* ====== TEACHERS ====== */}
          {activeTab === 'teachers' && (
            <div style={styles.card}>
              <table>
                <thead style={styles.tableHead}>
                  <tr>
                    {['Teacher', 'Email', 'Subjects', 'TRCN No.', 'Verification', 'EDU', 'Naira', 'Account', 'Actions'].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {teachers.map(t => (
                    <tr key={t.id}>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={styles.avatar('#eef2ff', '#4f46e5')}>{(t.firstName || 'T').charAt(0)}</div>
                          <span style={{ fontWeight: 500 }}>{t.firstName} {t.lastName}</span>
                        </div>
                      </td>
                      <td style={{ ...styles.td, color: '#64748b', fontSize: '0.8rem' }}>{t.email}</td>
                      <td style={{ ...styles.td, color: '#64748b', fontSize: '0.78rem', maxWidth: 140 }}>
                        {(t.subjects || []).slice(0, 2).join(', ')}{(t.subjects || []).length > 2 && ` +${t.subjects.length - 2}`}
                      </td>
                      <td style={{ ...styles.td, color: '#64748b', fontSize: '0.78rem' }}>{t.trcnNumber || '—'}</td>
                      <td style={styles.td}>
                        <span style={styles.pill(
                          t.isVerified ? '#ecfdf5' : t.trcnStatus === 'rejected' ? '#fef2f2' : t.trcnExamRegistered ? '#ecfeff' : '#fffbeb',
                          t.isVerified ? '#10b981' : t.trcnStatus === 'rejected' ? '#ef4444' : t.trcnExamRegistered ? '#0891b2' : '#f59e0b'
                        )}>
                          {t.isVerified ? '✅ Verified' : t.trcnStatus === 'rejected' ? 'Rejected' : t.trcnExamRegistered ? 'Exam Pending' : 'Unverified'}
                        </span>
                      </td>
                      <td style={styles.td}><span style={{ color: '#7c3aed', fontWeight: 600 }}>{t.tokenBalance || 0}</span></td>
                      <td style={{ ...styles.td, color: '#10b981', fontWeight: 600 }}>₦{(t.nairaBalance || 0).toLocaleString()}</td>
                      <td style={styles.td}>
                        <span style={styles.pill(t.status === 'suspended' ? '#fef2f2' : '#ecfdf5', t.status === 'suspended' ? '#ef4444' : '#10b981')}>
                          {t.status || 'active'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {!t.isVerified && t.trcnNumber && t.trcnStatus !== 'rejected' && (
                            <>
                              <button className="action-btn" style={styles.btn('success')} onClick={() => handleVerifyTeacher(t.id)} disabled={actionLoading}>Verify</button>
                              <button className="action-btn" style={styles.btn('danger')} onClick={() => { setSelectedUser(t); setShowRejectModal(true); }} disabled={actionLoading}>Reject</button>
                            </>
                          )}
                          {t.status === 'suspended'
                            ? <button className="action-btn" style={{ ...styles.btn('ghost'), color: '#10b981' }} onClick={() => handleActivateUser(t.id)} disabled={actionLoading}>Activate</button>
                            : <button className="action-btn" style={styles.btn('warning')} onClick={() => handleSuspendUser(t.id)} disabled={actionLoading}>Suspend</button>
                          }
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {teachers.length === 0 && <div style={styles.emptyState}>No teachers found</div>}
            </div>
          )}

          {/* ====== TRCN EXAMS ====== */}
          {activeTab === 'trcn' && (
            <div>
              <div style={{ ...styles.infoBox('#fffbeb', '#92400e'), marginBottom: 20 }}>
                <i className="fas fa-info-circle" style={{ marginRight: 8 }}></i>
                These teachers selected <strong>"No, register for TRCN exam"</strong> during signup. Contact them with exam details, schedule their exam, and update status accordingly.
              </div>
              {trcnRegistrations.length === 0 ? (
                <div style={{ ...styles.card, ...styles.emptyState }}>
                  <i className="fas fa-check-circle" style={{ fontSize: '2rem', color: '#10b981', marginBottom: 12, display: 'block' }}></i>
                  <strong>No pending TRCN exam registrations</strong>
                  <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>All teachers have been processed</p>
                </div>
              ) : (
                <div style={styles.card}>
                  <table>
                    <thead style={styles.tableHead}>
                      <tr>
                        {['Teacher', 'Email', 'Phone', 'Subjects', 'Qualification', 'State', 'Exam Status', 'Registered', 'Actions'].map(h => (
                          <th key={h} style={styles.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {trcnRegistrations.map(t => (
                        <tr key={t.id}>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={styles.avatar('#ecfeff', '#0891b2')}>{(t.firstName || 'T').charAt(0)}</div>
                              <span style={{ fontWeight: 500 }}>{t.firstName} {t.lastName}</span>
                            </div>
                          </td>
                          <td style={{ ...styles.td, color: '#64748b', fontSize: '0.8rem' }}>{t.email}</td>
                          <td style={{ ...styles.td, color: '#64748b', fontSize: '0.8rem' }}>{t.phone || '—'}</td>
                          <td style={{ ...styles.td, color: '#64748b', fontSize: '0.78rem' }}>
                            {(t.subjects || []).slice(0, 2).join(', ')}{(t.subjects || []).length > 2 && ` +${t.subjects.length - 2}`}
                          </td>
                          <td style={{ ...styles.td, color: '#64748b', fontSize: '0.78rem' }}>
                            {t.trcnExamRegistration?.qualification || t.qualification || '—'}
                          </td>
                          <td style={{ ...styles.td, color: '#64748b', fontSize: '0.78rem' }}>{t.state || '—'}</td>
                          <td style={styles.td}>
                            <span style={styles.pill(
                              t.trcnExamStatus === 'passed' ? '#ecfdf5' : t.trcnExamStatus === 'failed' ? '#fef2f2' : t.trcnExamStatus === 'scheduled' ? '#ecfeff' : '#fffbeb',
                              t.trcnExamStatus === 'passed' ? '#10b981' : t.trcnExamStatus === 'failed' ? '#ef4444' : t.trcnExamStatus === 'scheduled' ? '#0891b2' : '#f59e0b'
                            )}>
                              {t.trcnExamStatus || 'Not Scheduled'}
                            </span>
                            {t.trcnExamDate && <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 2 }}>{t.trcnExamDate}</div>}
                          </td>
                          <td style={{ ...styles.td, color: '#64748b', fontSize: '0.8rem' }}>{formatDate(t.createdAt)}</td>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button className="action-btn" style={styles.btn('primary')}
                                onClick={() => { setSelectedTRCN(t); setTrcnForm({ status: t.trcnExamStatus || '', examDate: t.trcnExamDate || '', notes: t.trcnExamNotes || '' }); setShowTRCNModal(true); }}
                                disabled={actionLoading}>Update</button>
                              {t.trcnExamStatus === 'passed' && !t.isVerified && (
                                <button className="action-btn" style={styles.btn('success')} onClick={() => handleVerifyTeacher(t.id)} disabled={actionLoading}>Verify</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ====== SESSIONS ====== */}
          {activeTab === 'sessions' && (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {['all', 'scheduled', 'ongoing', 'completed'].map(s => (
                  <button key={s} className="filter-btn" style={styles.filterBtn(sessionStatusFilter === s)}
                    onClick={() => { setSessionStatusFilter(s); fetchSessions(s); }}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
              <div style={styles.card}>
                <table>
                  <thead style={styles.tableHead}>
                    <tr>
                      {['Teacher', 'Student', 'Subject', 'Type', 'Date', 'Duration', 'Payment', 'Status'].map(h => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSessions.map(s => {
                      const isPaid = s.sessionTypeCategory === 'paid' || s.parentId;
                      return (
                        <tr key={s.id}>
                          <td style={styles.td}><span style={{ fontWeight: 500 }}>{s.teacherName || '—'}</span></td>
                          <td style={{ ...styles.td, color: '#64748b' }}>{s.studentName || s.childName || '—'}</td>
                          <td style={styles.td}><span style={styles.pill('#f1f5f9', '#475569')}>{s.subject}</span></td>
                          <td style={styles.td}><span style={{ color: isPaid ? '#10b981' : '#f59e0b', fontWeight: 600, fontSize: '0.78rem' }}>{isPaid ? 'Paid' : 'Volunteer'}</span></td>
                          <td style={{ ...styles.td, color: '#64748b', fontSize: '0.8rem' }}>{s.scheduleDate || '—'}<br /><span style={{ opacity: 0.7 }}>{s.scheduleTime || ''}</span></td>
                          <td style={{ ...styles.td, color: '#64748b', fontSize: '0.8rem' }}>{s.duration || 60}m</td>
                          <td style={styles.td}>
                            {isPaid
                              ? <span style={{ color: '#10b981', fontWeight: 600 }}>₦{((s.hourlyRate || 0) * (s.duration || 60) / 60).toLocaleString()}</span>
                              : <span style={{ color: '#7c3aed', fontWeight: 600 }}>{Math.floor((s.duration || 60) / 60 * 10)} EDU</span>
                            }
                          </td>
                          <td style={styles.td}>
                            <span style={styles.pill(
                              s.status === 'completed' ? '#ecfdf5' : s.status === 'ongoing' ? '#ecfeff' : s.status === 'scheduled' ? '#eef2ff' : '#f1f5f9',
                              s.status === 'completed' ? '#10b981' : s.status === 'ongoing' ? '#0891b2' : s.status === 'scheduled' ? '#6366f1' : '#64748b'
                            )}>{s.status}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredSessions.length === 0 && <div style={styles.emptyState}>No sessions found</div>}
              </div>
            </div>
          )}

          {/* ====== SESSION VIDEOS ====== */}
          {activeTab === 'videos' && (
            <div>
              {sessionVideos.length === 0 ? (
                <div style={{ ...styles.card, ...styles.emptyState }}>
                  <i className="fas fa-video" style={{ fontSize: '2rem', marginBottom: 12, display: 'block', color: '#94a3b8' }}></i>
                  <strong>No session videos yet</strong>
                  <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>Videos uploaded by teachers after sessions will appear here</p>
                </div>
              ) : (
                <div style={styles.card}>
                  <table>
                    <thead style={styles.tableHead}>
                      <tr>
                        {['Teacher', 'Student', 'Subject', 'Session Date', 'Type', 'Payment', 'EDU Coins', 'Report', 'Video'].map(h => (
                          <th key={h} style={styles.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sessionVideos.map(s => {
                        const isPaid = s.sessionTypeCategory === 'paid' || s.parentId;
                        return (
                          <tr key={s.id}>
                            <td style={styles.td}><span style={{ fontWeight: 500 }}>{s.teacherName || '—'}</span></td>
                            <td style={{ ...styles.td, color: '#64748b' }}>{s.studentName || s.childName || '—'}</td>
                            <td style={styles.td}><span style={styles.pill('#f1f5f9', '#475569')}>{s.subject}</span></td>
                            <td style={{ ...styles.td, color: '#64748b', fontSize: '0.8rem' }}>{s.scheduleDate || '—'}</td>
                            <td style={styles.td}><span style={{ color: isPaid ? '#10b981' : '#f59e0b', fontWeight: 600, fontSize: '0.78rem' }}>{isPaid ? 'Paid' : 'Volunteer'}</span></td>
                            <td style={styles.td}>
                              <span style={styles.pill(s.paymentProcessed ? '#ecfdf5' : '#fffbeb', s.paymentProcessed ? '#10b981' : '#f59e0b')}>
                                {s.paymentProcessed ? 'Paid' : 'Pending'}
                              </span>
                            </td>
                            <td style={styles.td}>
                              {s.coinsAllocated
                                ? <span style={{ color: '#7c3aed', fontWeight: 600, fontSize: '0.8rem' }}>T:{s.teacherCoins} S:{s.studentCoins}</span>
                                : <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Not allocated</span>
                              }
                            </td>
                            <td style={styles.td}>
                              <span style={styles.pill(s.teacherReportSubmitted ? '#ecfdf5' : '#fffbeb', s.teacherReportSubmitted ? '#10b981' : '#f59e0b')}>
                                {s.teacherReportSubmitted ? 'Submitted' : 'Pending'}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <a href={s.sessionVideo} target="_blank" rel="noopener noreferrer"
                                style={{ ...styles.btn('primary'), textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <i className="fas fa-play" style={{ fontSize: '0.7rem' }}></i> Watch
                              </a>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ====== WITHDRAWALS ====== */}
          {activeTab === 'withdrawals' && (
            <div>
              {withdrawals.length === 0 ? (
                <div style={{ ...styles.card, ...styles.emptyState }}>
                  <i className="fas fa-check-circle" style={{ fontSize: '2rem', color: '#10b981', marginBottom: 12, display: 'block' }}></i>
                  <strong>No pending withdrawals</strong>
                  <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>All requests have been processed</p>
                </div>
              ) : (
                <div style={styles.card}>
                  <table>
                    <thead style={styles.tableHead}>
                      <tr>
                        {['Teacher', 'Amount', 'Bank', 'Account Number', 'Account Name', 'Requested', 'Actions'].map(h => (
                          <th key={h} style={styles.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {withdrawals.map(w => (
                        <tr key={w.id}>
                          <td style={styles.td}><span style={{ fontWeight: 500 }}>{w.teacherName || '—'}</span></td>
                          <td style={styles.td}><span style={{ color: '#10b981', fontWeight: 700, fontSize: '1rem' }}>₦{(w.amount || 0).toLocaleString()}</span></td>
                          <td style={{ ...styles.td, color: '#64748b' }}>{w.bankName}</td>
                          <td style={{ ...styles.td, color: '#64748b', fontFamily: 'monospace' }}>{w.accountNumber}</td>
                          <td style={{ ...styles.td, color: '#64748b' }}>{w.accountName}</td>
                          <td style={{ ...styles.td, color: '#64748b', fontSize: '0.8rem' }}>{formatDate(w.requestedAt)}</td>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button className="action-btn" style={styles.btn('success')} onClick={() => handleApproveWithdrawal(w.id)} disabled={actionLoading}>✅ Approve</button>
                              <button className="action-btn" style={styles.btn('danger')} onClick={() => { setSelectedWithdrawal(w); setShowRejectModal(true); }} disabled={actionLoading}>❌ Reject</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ====== EDU COINS ====== */}
          {activeTab === 'educoins' && eduCoins && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                {[
                  { label: 'Total Issued', value: `${(eduCoins.totalIssued || 0).toLocaleString()} EDU`, color: '#7c3aed', bg: '#f5f3ff', icon: 'fa-coins' },
                  { label: 'In Wallets Now', value: `${(eduCoins.totalInWallets || 0).toLocaleString()} EDU`, color: '#0891b2', bg: '#ecfeff', icon: 'fa-wallet' },
                  { label: 'Session Rewards', value: `${(eduCoins.sessionRewards || 0).toLocaleString()} EDU`, color: '#10b981', bg: '#ecfdf5', icon: 'fa-chalkboard-teacher' },
                  { label: 'Signup Bonuses', value: `${(eduCoins.signupBonuses || 0).toLocaleString()} EDU`, color: '#f59e0b', bg: '#fffbeb', icon: 'fa-gift' },
                ].map((c, i) => (
                  <div key={i} style={styles.statCard(c.color, c.bg)}>
                    <div style={styles.statIcon(c.bg)}>
                      <i className={`fas ${c.icon}`} style={{ color: c.color }}></i>
                    </div>
                    <div>
                      <div style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{c.value}</div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 4 }}>{c.label}</div>
                    </div>
                  </div>
                ))}
              </div>

              <h6 style={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', marginBottom: 12 }}>Top EDU Coin Holders</h6>
              <div style={styles.card}>
                <table>
                  <thead style={styles.tableHead}>
                    <tr>
                      {['Rank', 'Name', 'Type', 'EDU Balance'].map(h => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(eduCoins.topHolders || []).map((h, i) => (
                      <tr key={h.id}>
                        <td style={styles.td}>
                          <span style={{ fontWeight: 700, color: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : '#64748b' }}>
                            #{i + 1}
                          </span>
                        </td>
                        <td style={styles.td}><span style={{ fontWeight: 500 }}>{h.name || '—'}</span></td>
                        <td style={styles.td}>
                          <span style={styles.pill(
                            h.userType === 'teacher' ? '#eef2ff' : '#ecfdf5',
                            h.userType === 'teacher' ? '#4f46e5' : '#10b981'
                          )}>{h.userType}</span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ color: '#7c3aed', fontWeight: 700, fontSize: '1rem' }}>{(h.tokenBalance || 0).toLocaleString()} EDU</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      </main>

      {/* ====== REJECT MODAL ====== */}
      {showRejectModal && (
        <div style={styles.modal} onClick={closeRejectModal}>
          <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
                {selectedWithdrawal ? 'Reject Withdrawal' : 'Reject TRCN Verification'}
              </h2>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.2rem' }} onClick={closeRejectModal}>×</button>
            </div>
            <div style={styles.modalBody}>
              {selectedWithdrawal && (
                <div style={styles.infoBox('#fff7ed', '#92400e')}>
                  <i className="fas fa-exclamation-triangle" style={{ marginRight: 8 }}></i>
                  <strong>₦{(selectedWithdrawal.amount || 0).toLocaleString()}</strong> will be automatically refunded to <strong>{selectedWithdrawal.teacherName}'s</strong> wallet.
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.label}>Reason (sent to teacher)</label>
                <textarea style={{ ...styles.textarea, minHeight: 80 }} rows={3}
                  value={rejectionReason} onChange={e => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection..." />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.btn('ghost')} onClick={closeRejectModal}>Cancel</button>
              <button style={{ ...styles.btn('primary'), background: '#ef4444' }}
                onClick={selectedWithdrawal ? confirmRejectWithdrawal : confirmRejectVerification}
                disabled={actionLoading}>
                {actionLoading ? 'Processing...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== TRCN MODAL ====== */}
      {showTRCNModal && selectedTRCN && (
        <div style={styles.modal} onClick={() => { setShowTRCNModal(false); setSelectedTRCN(null); }}>
          <div style={styles.modalBoxLg} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
                TRCN Exam — {selectedTRCN.firstName} {selectedTRCN.lastName}
              </h2>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.2rem' }}
                onClick={() => { setShowTRCNModal(false); setSelectedTRCN(null); }}>×</button>
            </div>
            <div style={styles.modalBody}>
              {/* Teacher summary */}
              <div style={{ ...styles.infoBox('#f8fafc', '#0f172a'), display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                {[
                  { label: 'Email', value: selectedTRCN.email },
                  { label: 'Phone', value: selectedTRCN.phone || '—' },
                  { label: 'State', value: `${selectedTRCN.state || '—'}, ${selectedTRCN.localGovernment || '—'}` },
                  { label: 'Qualification', value: selectedTRCN.trcnExamRegistration?.qualification || selectedTRCN.qualification || '—' },
                  { label: 'Subjects', value: (selectedTRCN.subjects || []).join(', ') || '—' },
                  { label: 'Experience', value: selectedTRCN.trcnExamRegistration?.teachingExperience ? selectedTRCN.trcnExamRegistration.teachingExperience.substring(0, 60) + '...' : '—' },
                ].map((item, i) => (
                  <div key={i}>
                    <span style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block' }}>{item.label}</span>
                    <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#0f172a' }}>{item.value}</span>
                  </div>
                ))}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Exam Status *</label>
                <select style={styles.select} value={trcnForm.status} onChange={e => setTrcnForm({ ...trcnForm, status: e.target.value })}>
                  <option value="">Select status...</option>
                  <option value="scheduled">📅 Scheduled — Exam date confirmed</option>
                  <option value="passed">✅ Passed — Teacher passed the exam</option>
                  <option value="failed">❌ Failed — Teacher did not pass</option>
                </select>
              </div>

              {trcnForm.status === 'scheduled' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Exam Date</label>
                  <input type="date" style={styles.input} value={trcnForm.examDate} onChange={e => setTrcnForm({ ...trcnForm, examDate: e.target.value })} />
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>Notes (sent to teacher)</label>
                <textarea style={{ ...styles.textarea, minHeight: 80 }} rows={3}
                  value={trcnForm.notes} onChange={e => setTrcnForm({ ...trcnForm, notes: e.target.value })}
                  placeholder="Add instructions or feedback for the teacher..." />
              </div>

              {trcnForm.status === 'passed' && (
                <div style={styles.infoBox('#ecfdf5', '#065f46')}>
                  <i className="fas fa-info-circle" style={{ marginRight: 8 }}></i>
                  After saving, go to the <strong>Teachers</strong> tab to grant this teacher the verified badge.
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.btn('ghost')} onClick={() => { setShowTRCNModal(false); setSelectedTRCN(null); }}>Cancel</button>
              <button style={styles.btn('primary')} onClick={handleUpdateTRCN} disabled={actionLoading || !trcnForm.status}>
                {actionLoading ? 'Saving...' : 'Save & Notify Teacher'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminDashboard;